import { Trans } from '@lingui/react'
import {
  type Dispatch,
  type FC,
  type SetStateAction,
  useEffect,
  useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { SA_SELLER_ID } from 'shared/consts'
import { fetchCartAction } from 'shared/store/ducks/cart'
import { updateNowDataSuccessAction } from 'shared/store/ducks/now/actions'
import { NOW_DATA_KEY, NOW_HOME_ONE_DATA_KEY } from 'shared/store/helper/consts'
import {
  type CartControllerCartEntryItemERx,
  type CartControllerCartEntryItemV2,
  type CartControllerSellerGroupV2,
} from 'types/api/cart'
import { type IState, type NowDataState } from 'types/redux'
import NowNeutralCaseBox from 'views/components/molecules/NowNeutralCaseBox'
import Collapsible from 'views/components/organisms/Collapsible'
import { POSITION_LAST } from 'views/components/organisms/Collapsible/consts'
import { NOT_IN_DELIVERY_REGION } from 'views/consts/notEligibleReasons'
import buildClientAPI from 'views/providers/clientAPI'
import { getJsonDataFromLocalStorage } from 'views/utils/getJsonDataFromLocalStorage'

import WithZipCodeCase from './WithZipCodeCase'

import './NowCollapsible.scss'

type Props = {
  hasNowProducts: boolean
  setHasNowProducts: Dispatch<SetStateAction<boolean>>
  setIsRedpointsInTheMiddleWithNow: Dispatch<SetStateAction<boolean>>
}

// Backport from https://github.com/RedTecLab/home-one/blob/main/app/domain/now/context/nowGeneralContext.tsx:20
type NowInfoStorage = {
  zipSupportInfo?: {
    checkedAt?: number
    inDeliveryRegion?: boolean
    zip?: string
  }
}

const SAME_OR_NEXT_DAY = 'sameday_nextday'

const groupEntryIsNowEntry = (entry: CartControllerCartEntryItemV2) =>
  Boolean(
    entry.nowItemData?.nowProduct || entry.nowProductGroup === SAME_OR_NEXT_DAY
  )

const erxGroupEntryIsNowEntry = (entry: CartControllerCartEntryItemERx) =>
  Boolean(entry.nowItemData?.nowProduct)

const groupEntriesHaveNowEntry = (
  sellerGroup: CartControllerSellerGroupV2 | undefined
) =>
  Boolean(
    sellerGroup?.groupEntries.some(group =>
      group.items.some(groupEntryIsNowEntry)
    )
  )

const erxGroupEntriesHaveNowEntry = (
  sellerGroup: CartControllerSellerGroupV2 | undefined
) =>
  Boolean(
    sellerGroup?.groupEntriesERx.some(group =>
      group.items.some(erxGroupEntryIsNowEntry)
    )
  )

const NowCollapsible: FC<Props> = (props: Props) => {
  const {
    hasNowProducts,
    setHasNowProducts,
    setIsRedpointsInTheMiddleWithNow,
  } = props

  const defaultNowData: NowDataState = useSelector(
    (state: IState) => state.nowData
  )

  const loggedInUser = useSelector(
    (state: IState) => state.userSession.newLogin
  )

  const cart = useSelector((state: IState) => state.cart.data)

  const [nowData, setNowData] = useState(defaultNowData)

  const [zipCode, setZipCode] = useState(nowData?.zipCode)

  const dispatch = useDispatch()

  useEffect(() => {
    const sellerGroupSAE = cart.sellerGroups.find(
      it => it.sellerId === SA_SELLER_ID
    )

    const cartHasNowProducts =
      groupEntriesHaveNowEntry(sellerGroupSAE) ||
      erxGroupEntriesHaveNowEntry(sellerGroupSAE)

    const checkIfNowProductsInCart = Boolean(
      defaultNowData?.products?.some(item => item.nowProduct)
    )

    const nowProductsAvailable = !!(
      checkIfNowProductsInCart ||
      cartHasNowProducts ||
      defaultNowData?.nowEligible
    )

    setHasNowProducts(nowProductsAvailable)
    setIsRedpointsInTheMiddleWithNow(
      nowProductsAvailable ||
        defaultNowData?.notEligibleReason === NOT_IN_DELIVERY_REGION
    )
  }, [
    cart.sellerGroups,
    defaultNowData,
    setHasNowProducts,
    setIsRedpointsInTheMiddleWithNow,
  ])

  useEffect(() => {
    // Getting Now ZIP info from Home-One data in case if it exists
    // https://github.com/RedTecLab/home-one/blob/main/app/domain/now/context/nowGeneralContext.tsx
    const homeOneNowData =
      getJsonDataFromLocalStorage<NowInfoStorage>(NOW_HOME_ONE_DATA_KEY) ?? {}
    const payload: NowDataState =
      getJsonDataFromLocalStorage<NowDataState>(NOW_DATA_KEY) ?? {}
    if (homeOneNowData?.zipSupportInfo?.zip && !payload?.zipCode) {
      payload.zipCode = homeOneNowData?.zipSupportInfo.zip
    }

    dispatch(updateNowDataSuccessAction(payload))

    const getBillingAddress = async () => {
      if (payload?.zipCode) {
        setZipCode(payload.zipCode)
      }

      if (!payload?.zipCode && loggedInUser && payload?.zipCode !== '') {
        const clientApi = buildClientAPI()

        const billingAddress =
          await clientApi.addressService.getBillingAddress()

        if (billingAddress?.zip) {
          setZipCode(billingAddress.zip)

          dispatch(
            fetchCartAction(true, false, {
              nowZipCode: billingAddress.zip,
              isNowSelected: false,
            })
          )

          return
        }
      }

      dispatch(
        fetchCartAction(true, false, {
          nowZipCode: payload?.zipCode,
          isNowSelected: false,
        })
      )
    }

    void getBillingAddress()
  }, [dispatch, loggedInUser])

  useEffect(() => {
    setNowData(defaultNowData)
    setZipCode(defaultNowData?.zipCode)
  }, [defaultNowData])

  const isExpanded = true

  setIsRedpointsInTheMiddleWithNow(hasNowProducts)

  return (
    <div hidden={!hasNowProducts}>
      <Collapsible
        id="now-collapsible"
        label={<Trans id="general.label.now.collapsible.title" />}
        allScreensizes
        light
        position={POSITION_LAST}
        forceDisplayWhenCollapsed
        expanded={isExpanded}
      >
        <div className={'o-NowCollapsible'}>
          <div hidden={!zipCode}>
            <WithZipCodeCase cart={cart} nowData={nowData} />
          </div>
          <div hidden={!!zipCode}>
            <NowNeutralCaseBox hasNowEligilityCheck />
          </div>
        </div>
      </Collapsible>
    </div>
  )
}

export default NowCollapsible
